// File: frontend/src/hooks/useChatState.js

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config/constants';

// Helper functions for message handling
const createUserMessage = (message) => ({
  role: 'user',
  content: [{
    type: 'text',
    text: message
  }],
  timestamp: new Date().toISOString(),
  id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Add unique ID
});

const createTypingIndicator = () => ({
  role: 'assistant',
  content: [{
    type: 'text',
    text: ''
  }],
  isTyping: true,
  timestamp: new Date().toISOString(),
  id: `typing-${Date.now()}` // Add unique ID
});

const createAssistantMessage = (response) => ({
  role: 'assistant',
  content: response.message,
  timestamp: new Date().toISOString(),
  usage: response.usage,
  id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Add unique ID
});

export const useChatState = (initialChatId = null, initialMessages = []) => {
  const [chatId, setChatId] = useState(initialChatId);
  const [messages, setMessages] = useState(() => {
    // Add unique IDs to initial messages if they don't have them
    return initialMessages.map(msg => 
      msg.id ? msg : { ...msg, id: `${msg.role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` }
    );
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingChat, setIsFetchingChat] = useState(false);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const navigate = useNavigate();
  const navigationInProgress = useRef(false);
  const pendingMessageRef = useRef(null);

  // Store the latest messages in a ref to avoid closure issues
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Function to check for duplicate messages
  const isDuplicateMessage = (newMessage) => {
    return messagesRef.current.some(msg => 
      msg.role === newMessage.role && 
      msg.content[0]?.text === newMessage.content[0]?.text &&
      !msg.isTyping
    );
  };

  const sendMessage = useCallback(async (message, templateId, agentKey) => {
    if (!message.trim() || isMessageSending) return;

    // Create the user message
    const userMessage = createUserMessage(message);
    
    // Check if this is a duplicate message
    if (isDuplicateMessage(userMessage)) {
      console.warn('Duplicate message detected, ignoring:', message);
      return;
    }

    setIsMessageSending(true);
    const typingIndicator = createTypingIndicator();

    // Store the message we're about to send
    pendingMessageRef.current = message;

    // Immediately update UI with user message and typing indicator
    setMessages(prev => [...prev, userMessage, typingIndicator]);

    try {
      let currentChatId = chatId;
      
      // If no chat ID exists, initialize chat
      if (!currentChatId) {
        navigationInProgress.current = true;
        
        try {
          const chatResponse = await apiService.post(API_ENDPOINTS.AGENTS.CHAT.INITIATE, {
            template_id: templateId,
            agent_name: agentKey,
            training_mode: false,
            source: 'ui'
          });

          currentChatId = chatResponse.chat_id;
          setChatId(currentChatId);
          
          // Update URL without causing a reload
          navigate(`/ai1/chat/${currentChatId}`, { 
            replace: true,
            state: { 
              preserveState: true 
            }
          });
        } catch (error) {
          console.error('Error initializing chat:', error);
          // Remove typing indicator on error
          setMessages(prev => prev.filter(msg => !msg.isTyping));
          setIsMessageSending(false);
          navigationInProgress.current = false;
          pendingMessageRef.current = null;
          return;
        }
      }

      // Send message with chat ID (either existing or new)
      const messageResponse = await apiService.post(API_ENDPOINTS.AGENTS.CHAT.MESSAGE, {
        chat_id: currentChatId,
        message
      });
      
      if (messageResponse) {
        // Update messages, replacing typing indicator
        setMessages(prev => {
          // Filter out typing indicators and any duplicate user messages
          const filteredMessages = prev.filter(msg => 
            !msg.isTyping && 
            !(msg.role === 'user' && 
              msg.content[0]?.text === message && 
              msg !== userMessage)
          );
          
          return [...filteredMessages, createAssistantMessage(messageResponse)];
        });
      }
      
      // Reset navigation flag after a short delay
      if (navigationInProgress.current) {
        setTimeout(() => {
          navigationInProgress.current = false;
        }, 300);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove typing indicator on error
      setMessages(prev => prev.filter(msg => !msg.isTyping));
      navigationInProgress.current = false;
    } finally {
      setIsMessageSending(false);
      pendingMessageRef.current = null;
    }
  }, [chatId, navigate, isMessageSending]);

  const fetchChatHistory = useCallback(async (chatId) => {
    if (!chatId) return null;
    
    try {
      setIsFetchingChat(true);
      const response = await apiService.get(`/agents/chat/${chatId}`);
      
      if (response) {
        setChatId(chatId);
        
        // Only update messages if we don't already have them or if we have a pending message
        if (!messagesRef.current.length || pendingMessageRef.current) {
          // Add unique IDs to messages if they don't have them
          const messagesWithIds = (response.messages || []).map(msg => 
            msg.id ? msg : { ...msg, id: `${msg.role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` }
          );
          
          setMessages(messagesWithIds);
        }
        return response;
      }
      return null;
    } catch (error) {
      console.error('Error fetching chat:', error);
      throw error;
    } finally {
      setIsFetchingChat(false);
    }
  }, []);

  const resetChat = useCallback(() => {
    setChatId(null);
    setMessages([]);
    pendingMessageRef.current = null;
  }, []);

  // Clean up any stuck typing indicators after a timeout
  useEffect(() => {
    const typingTimeout = setTimeout(() => {
      const hasTypingIndicator = messages.some(msg => msg.isTyping);
      
      if (hasTypingIndicator && !isMessageSending) {
        console.log('Cleaning up stuck typing indicator');
        setMessages(prev => prev.filter(msg => !msg.isTyping));
      }
    }, 15000); // 15 seconds timeout for typing indicators
    
    return () => clearTimeout(typingTimeout);
  }, [messages, isMessageSending]);

  return {
    chatId,
    messages,
    isLoading,
    isFetchingChat,
    isMessageSending,
    sendMessage,
    fetchChatHistory,
    resetChat,
    setMessages,
    navigationInProgress: navigationInProgress.current
  };
};